import {
  AccordionComponent,
  AccordionSummaryComponent,
  AccordionDetailsComponent,
} from 'app/components/StyledComponents';

import {
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Box,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

export interface SegmentRow {
  field_name?: string;
  field_value?: string;
  qualifier?: string;
  primary_segment?: string;
  secondary_segment?: string;
  tertiary_segment?: string;
  priority?: string | number;
}

interface GroupedSection {
  primarySegment: string;
  items: { row: SegmentRow; index: number }[];
}

interface SegmentSettingsTableProps {
  groupedSections: GroupedSection[];
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  loading?: boolean;
  ariaLabelPrefix?: string;
}

export const SegmentSettingsTable = ({
  groupedSections,
  onEdit,
  onDelete,
  loading = false,
  ariaLabelPrefix = 'segments',
}: SegmentSettingsTableProps) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {groupedSections.map(({ primarySegment, items }) => (
        <AccordionComponent
          key={primarySegment}
          elevation={0}
          sx={{
            '&::before': { display: 'none' },
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.12)',
            borderRadius: '4px !important',
          }}
        >
          <AccordionSummaryComponent
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`primary-segment-${primarySegment}-content`}
            id={`primary-segment-${primarySegment}-header`}
            sx={{ 
              paddingLeft: 2,
              minHeight: 48,
              '&.Mui-expanded': {
                minHeight: 48,
              },
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
            >
              Primary Segment - {primarySegment}
            </Typography>
          </AccordionSummaryComponent>
          <AccordionDetailsComponent
            sx={{
              padding: 0,
              paddingTop: 0,
            }}
          >
            <Box sx={{ overflow: 'auto', width: '100%' }}>
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label={`${ariaLabelPrefix} ${primarySegment}`}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Field Name</TableCell>
                    <TableCell>Field Value</TableCell>
                    <TableCell>Qualifier</TableCell>
                    <TableCell>Secondary Segment</TableCell>
                    <TableCell>Tertiary Segment</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(({ row, index }) => (
                    <TableRow key={index}>
                      <TableCell>{row.field_name ?? '—'}</TableCell>
                      <TableCell>{row.field_value ?? '—'}</TableCell>
                      <TableCell>{row.qualifier ?? '—'}</TableCell>
                      <TableCell>{row.secondary_segment ?? '—'}</TableCell>
                      <TableCell>{row.tertiary_segment ?? '—'}</TableCell>
                      <TableCell>{row.priority ?? '—'}</TableCell>
                      <TableCell>
                        <IconButton
                          aria-label="edit"
                          onClick={() => onEdit(index)}
                          disabled={loading}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          aria-label="delete"
                          onClick={() => onDelete(index)}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetailsComponent>
        </AccordionComponent>
      ))}
    </Box>
  );
};
